import { computed, ref, watch } from 'vue';
import type { ResolvedThemeRuntime } from 'shared';
import { defineStore } from 'pinia';
import { applyDocumentTheme } from '../../../design-system/theme';
import { useAuthStore } from '@/stores/auth.store';
import {
  cacheAppearance,
  defaultAppearance,
  readCachedAppearance,
  workspaceAntTheme,
  workspaceThemeStyle,
} from '@/utils/workspace-theme';
import { themeApi } from '@/api/theme.api';

export const useWorkspaceThemeStore = defineStore('workspace-theme', () => {
  const auth = useAuthStore();
  const cachedAppearance = ref(readCachedAppearance());
  const runtime = ref<ResolvedThemeRuntime | null>(null);
  const appearance = computed(() => runtime.value?.appearance ?? auth.user?.resolvedTheme?.appearance ?? auth.user?.appearance ?? cachedAppearance.value ?? defaultAppearance());
  const style = computed(() => workspaceThemeStyle(appearance.value, runtime.value ?? auth.user?.resolvedTheme));
  const antTheme = computed(() => workspaceAntTheme(appearance.value, runtime.value ?? auth.user?.resolvedTheme));

  function apply(): void {
    for (const [name, value] of Object.entries(style.value))
      if (typeof value === 'string') document.documentElement.style.setProperty(name, value);
    document.documentElement.dataset.awTransparency = runtime.value?.preferences.transparency ?? 'full';
    document.documentElement.dataset.themeEngine = 'v2';
    applyDocumentTheme(appearance.value.mode, {
      contrast: appearance.value.contrast,
      themeColor: appearance.value.canvasColor,
    });
  }

  watch(
    () => auth.user?.appearance,
    (value) => {
      if (!value) return;
      cachedAppearance.value = value;
      cacheAppearance(value);
    },
    { immediate: true },
  );
  watch(style, apply, { immediate: true });

  async function refreshRuntime(): Promise<void> {
    if (!auth.user) return;
    runtime.value = await themeApi.runtime();
  }
  function setRuntime(value: ResolvedThemeRuntime): void {
    runtime.value = value;
    cachedAppearance.value = value.appearance;
    cacheAppearance(value.appearance);
  }
  watch(() => auth.user?.id, () => { void refreshRuntime().catch(() => undefined); }, { immediate: true });

  return { appearance, style, antTheme, runtime, refreshRuntime, setRuntime };
});
